import { useCallback, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { BsCloudUpload } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';

const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB

export default function FileUploadStep({ isAnalyzing, onFileSelected, onCancel }) {
	const inputRef = useRef(null);
	const [dragActive, setDragActive] = useState(false);
	const [error, setError] = useState(null);
	const [fileName, setFileName] = useState(null);

	const handleFile = useCallback(
		(file) => {
			setError(null);
			if (!file.name.toLowerCase().endsWith('.csv')) {
				setError('Bitte laden Sie eine CSV-Datei hoch.');
				return;
			}
			if (file.size > MAX_FILE_SIZE) {
				setError('Die Datei ist zu groß (max. 15 MB).');
				return;
			}
			setFileName(file.name);
			onFileSelected(file);
		},
		[onFileSelected],
	);

	const handleDrop = useCallback(
		(e) => {
			e.preventDefault();
			setDragActive(false);
			const file = e.dataTransfer?.files?.[0];
			if (file) handleFile(file);
		},
		[handleFile],
	);

	const handleChange = useCallback(
		(e) => {
			const file = e.target.files?.[0];
			if (file) handleFile(file);
		},
		[handleFile],
	);

	if (isAnalyzing) {
		return (
			<div className="flex flex-col items-center justify-center py-16">
				<div className="w-12 h-12 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin mb-4" />
				<p className="text-md font-medium text-text">Datei wird analysiert...</p>
			</div>
		);
	}

	return (
		<div>
			<p className="text-sm text-text mb-4">
				Laden Sie eine CSV-Datei mit Kontakt- und Firmendaten hoch. Pro Zeile werden der Kontakt und seine Firma
				angelegt und automatisch verknüpft (Firma wird über die Domain erkannt).
			</p>

			<div
				className={`border-2 border-dashed rounded-card p-12 text-center transition-colors cursor-pointer ${
					dragActive ? 'border-primary-400 bg-primary-100' : 'border-bgBase-600 hover:border-bgBase-700'
				}`}
				onDragOver={(e) => {
					e.preventDefault();
					setDragActive(true);
				}}
				onDragLeave={() => setDragActive(false)}
				onDrop={handleDrop}
				onClick={() => inputRef.current?.click()}
			>
				<BsCloudUpload className="w-12 h-12 text-bgBase-800 mx-auto mb-4" />
				<p className="text-md font-medium text-text">CSV-Datei hierher ziehen oder klicken</p>
				<p className="text-sm text-bgBase-800 mt-1">Max. 15 MB, bis zu 10.000 Zeilen</p>
				{fileName && <p className="text-sm text-primary-600 mt-2 font-medium">{fileName}</p>}
				<input ref={inputRef} type="file" accept=".csv" className="hidden" onChange={handleChange} />
			</div>

			{error && (
				<div className="mt-4 p-3 bg-warning-200 border border-warning-500/40 rounded-card text-sm text-warning-700">
					{error}
				</div>
			)}

			<div className="flex justify-between mt-6">
				<Button variant="outline" onClick={onCancel}>
					Abbrechen
				</Button>
			</div>
		</div>
	);
}

FileUploadStep.propTypes = {
	isAnalyzing: PropTypes.bool,
	onFileSelected: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
};
